<template>
    <Dialog :visible="visible" modal dismissableMask :closable="false" :draggable="false"
        :style="{ width: '36rem', maxWidth: '95vw' }" contentClass="!p-0 overflow-hidden rounded-lg"
        :pt="{ header: { class: '!p-0' } }" @update:visible="v => !v && emit('close')">
        <!-- HEADER -->
        <template #header>
            <div :class="['flex items-center gap-4 w-full p-3 rounded-t-lg', isDelete ? 'bg-gradient-to-r from-red-50 to-rose-50' : mode === 'create' ? 'bg-gradient-to-r from-green-50 to-emerald-50' : 'bg-gradient-to-r from-blue-50 to-indigo-50']">
                <div :class="['w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0', isDelete ? 'bg-red-500' : mode === 'create' ? 'bg-green-500' : 'bg-blue-500']">
                    <i :class="['text-white text-xl', isDelete ? 'fa-regular fa-trash-can' : mode === 'create' ? 'fa-solid fa-plus' : 'fa-regular fa-pen-to-square']" />
                </div>

                <div class="flex-1 min-w-0">
                    <h4 class="font-bold mb-0">
                        {{ dialogTitle }}
                    </h4>
                </div>

                <Button icon=" fa-solid fa-xmark" rounded text severity="secondary" :aria-label="t('common.close')"
                    @click="emit('close')" />
            </div>
        </template>

        <!-- BODY -->
        <form @submit.prevent="save">
            <!-- DELETE WARNING -->
            <div v-if="isDelete"
                class="p-3">
                <p class="whitespace-pre-wrap">
                    {{ t('customFields.deleteWarning') }}
                </p>
            </div>

            <!-- FORM -->
            <div v-if="!isDelete" class="space-y-5 p-4">
                <!-- TITLE -->
                <div class="space-y-2">
                    <label class="font-semibold">
                        {{ t('common.title') }} <span class="text-red-500">*</span>
                    </label>
                    <InputText v-model="localField.title" required class="w-full" @blur="touched.title = true" />
                    <small v-if="validation.title.value.invalid" class="text-red-500">
                        {{ validation.title.value.message }}
                    </small>
                </div>

                <!-- DESCRIPTION -->
                <div class="space-y-2">
                    <label class="font-semibold">
                        {{ t('common.description') }}
                    </label>
                    <Textarea v-model="localField.description" rows="3" class="w-full" />
                </div>

                <!-- EMAIL -->
                <div class="space-y-2">
                    <label class="font-semibold">
                        {{ t('customFields.email') }}
                    </label>
                    <InputText v-model="localField.email" class="w-full" />
                </div>

                <!-- STATUS -->
                <div class="space-y-2">
                    <label class="font-semibold">
                        {{ t('common.status') }} <span class="text-red-500">*</span>
                    </label>
                    <Select v-model="localField.enabled" :options="[
                        { label: t('common.enabled'), value: true },
                        { label: t('common.disabled'), value: false }
                    ]" optionLabel="label" optionValue="value" class="w-full" @change="touched.enabled = true"
                        @blur="touched.enabled = true" />
                    <small v-if="validation.enabled.value.invalid" class="text-red-500">
                        {{ validation.enabled.value.message }}
                    </small>
                </div>
            </div>

            <!-- FOOTER -->
            <div class="flex justify-end p-3 border-t bg-gray-50">
                <Button v-if="isDelete" type="submit" :label="t('common.delete')" severity="danger" />
                <Button v-else type="submit" :label="mode === 'create' ? t('common.create') : t('common.save')" severity="success"
                    :disabled="!isFormValid" />
            </div>
        </form>
    </Dialog>
</template>

<script setup lang="ts">
import { CustomField } from '@/lib/types/customField'
import { useCustomFieldsStore } from '@/store/customField'
import { computed, reactive, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'

const { t } = useI18n()
const store = useCustomFieldsStore()

const props = defineProps<{
    visible: boolean
    mode: 'create' | 'edit' | 'delete'
    customField: CustomField | null
}>()

const emit = defineEmits(['close', 'saved'])

/* --------------------
   STATE
-------------------- */

// Track which fields have been touched for validation
const touched = reactive({
    title: false,
    enabled: false
})

// Field validation computed properties
const validation = {
    title: computed(() => ({
        invalid: touched.title && (!localField.value.title || localField.value.title.trim() === ''),
        message: t('customFields.validation.titleRequired')
    })),
    enabled: computed(() => ({
        invalid: touched.enabled && localField.value.enabled === undefined,
        message: t('customFields.validation.statusRequired')
    }))
}

// Touch all fields for validation on submit
function touchAllFields() {
    Object.keys(touched).forEach(key => {
        (touched as any)[key] = true
    })
}

// Reset touched state
function resetTouched() {
    Object.keys(touched).forEach(key => {
        (touched as any)[key] = false
    })
}

// Check if form is valid
const isFormValid = computed(() => {
    // Skip validation for delete mode
    if (isDelete.value) return true

    const titleValid = localField.value.title && localField.value.title.trim() !== ''
    const enabledValid = localField.value.enabled !== undefined

    return titleValid && enabledValid
})

function emptyField(): CustomField {
    return {
        id: '',
        title: '',
        description: '',
        email: '',
        enabled: true
    }
}

const localField = ref<CustomField>(emptyField())

/* --------------------
   COMPUTED
-------------------- */

const isDelete = computed(() => props.mode === 'delete')

const dialogTitle = computed(() =>
    props.mode === 'create'
        ? t('customFields.new')
        : props.mode === 'edit'
            ? t('customFields.edit')
            : t('customFields.delete')
)

/* --------------------
   WATCH
-------------------- */

watch(
    () => [props.visible, props.customField],
    ([visible, field]) => {
        if (!visible) return

        // Reset touched state
        resetTouched()

        if (field) {
            localField.value = Object.assign(emptyField(), field);
        } else {
            localField.value = emptyField();
        }
    },
    { immediate: true }
)

/* --------------------
   ACTIONS
-------------------- */

async function save() {
    // Touch all fields to show validation errors
    touchAllFields()

    // Check if form is valid (skip for delete mode)
    if (!isDelete.value && !isFormValid.value) {
        return
    }

    try {
        if (props.mode === 'create') {
            await store.createOne(localField.value)
        }
        else if (props.mode === 'edit') {
            await store.updateOne(localField.value)
        }
        else if (props.mode === 'delete') {
            await store.deleteOne(localField.value.id)
        }

        emit('saved')
        emit('close')
    } catch (err) {
        console.error('CustomField save failed:', err)
    }
}
</script>
